import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import Loader from 'src/@vymo/ui/atoms/loader';
import classnames from 'classnames';
import { ButtonProps } from './types';
import styles from './index.module.scss';

function renderButtonContent({
  children,
  iconProps,
  loading,
  type,
  size,
}: React.PropsWithChildren<ButtonProps>) {
  return (
    <>
      {iconProps && iconProps.icon && iconProps.iconPosition === 'left' && (
        <span data-testid="icon" className={styles.button__icon}>
          {iconProps.icon}
        </span>
      )}
      {children && (
        <span className={styles[`button__content__${size}`]}>{children}</span>
      )}
      {iconProps && iconProps.icon && iconProps.iconPosition === 'right' && (
        <span data-testid="icon" className={styles.button__icon}>
          {iconProps.icon}
        </span>
      )}
      {loading && (
        <Loader
          visible
          size="small"
          delay={0}
          color={type === 'primary' ? 'var(--color-neutral-100)' : undefined}
        />
      )}
    </>
  );
}

const Button = forwardRef<
  HTMLInputElement,
  React.PropsWithChildren<ButtonProps>
>(
  // eslint-disable-next-line complexity
  (
    {
      children,
      type = 'primary',
      size = 'medium',
      rounded = false,
      disabled = false,
      onClick = () => {},
      onMouseDown = () => {},
      linkProps,
      iconProps,
      variant = 'default',
      className,
      loading = false,
      'data-test-id': dataTestId,
      isIcon = false,
      ...props
    },
    ref,
  ) => {
    if (loading) {
      disabled = true;
    }

    const buttonRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => (buttonRef.current as any) ?? {}, []);

    const buttonClasses = classnames(
      styles.button,
      styles[`button__${type}`],
      styles[`button__${size}`],
      styles[`button__${variant}`],
      {
        [styles.button__rounded]: rounded,
        [styles.button__disabled]: disabled,
        [styles.button__iconLeft]:
          iconProps && iconProps.icon && iconProps.iconPosition === 'left',
        [styles.button__iconRight]:
          iconProps && iconProps.icon && iconProps.iconPosition === 'right',
        [styles[`button__outlined__${variant}`]]: type === 'outlined',
        [styles[`button__text__${variant}`]]: type === 'text',
        [styles[`button__link__${variant}`]]: type === 'link',
        [styles[`button__iconButton__${size}`]]: isIcon,
      },
      className,
    );

    const handleMouseUp = () => {
      // @ts-ignore
      buttonRef?.current?.blur?.();
    };

    if (type === 'link') {
      return (
        // TODO @swapnil make this button and link styles
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <a
          data-testid="link-button"
          data-test-id={dataTestId}
          {...linkProps}
          className={buttonClasses}
          onClick={onClick}
        >
          {renderButtonContent({ children, iconProps, loading, type, size })}
        </a>
      );
    }

    return (
      <button
        ref={buttonRef as any}
        type="button"
        className={buttonClasses}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseUp={handleMouseUp}
        disabled={disabled}
        data-testid="button"
        data-test-id={dataTestId}
        {...props}
      >
        {renderButtonContent({ children, iconProps, loading, type, size })}
      </button>
    );
  },
);

export default React.memo(Button);
